import { memo, useCallback, useMemo, useState } from "react";
import {
  DMAutomation,
  DMAutomationPlatform,
  DMAutomationState,
  DMAutomationContent,
  DMAutomationContentType,
  DMAutomationTrigger,
  InstagramTrigger,
  MassText,
  MassTextType,
  MassTextAttachment,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import LargePopupModalContainer from "../../../Containers/LargePopupModalContainer";
import DMAutomationCreateTrigger from "./DMAutomationCreateTrigger";
import CreateDMAutomation from "./CreateDMAutomation";
import DMAutomationContentPanel from "./DMAutomationContentPanel";
import DMAutomationMassTextPanel from "./DMAutomationMassTextPanel";
import DMAutomationPromptsPanel from "./DMAutomationPromptsPanel";
import {
  makeEmptyDMAutomationContent,
  makeEmptyDMAutomationTrigger,
  makeEmptyMassText,
  subscribeTriggerPlaceholder,
} from "@markit/common.utils";
import { generate } from "shortid";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { getCampaignState } from "../../../../redux/slices/campaignSlice";
import { fetchCampaignInitialMassText } from "../../../../utils/campaignUtils";

enum CreateDMAutomationStage {
  TRIGGER = "Trigger",
  AUTOMATION = "Automation",
  CONTENT = "Content",
  MASS_TEXT = "MassText",
  CUSTOMIZE_PROMPTS = "CustomizePrompts",
}

type CreateDMAutomationWrapperProps = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  existingDmAutomation?: DMAutomation; // For editing existing automation and for clone automation
  existingDmTriggers?: DMAutomationTrigger[];
  existingDmContents?: DMAutomationContent[];
  initialEditContent?: boolean;
  initialPlatform?: DMAutomationPlatform;
  initialTrigger?: DMAutomationTrigger; // Trigger type chosen in the select-trigger modal, for new automations
};

// The creation/editing flow for a DMAutomation
const CreateDMAutomationWrapper = (props: CreateDMAutomationWrapperProps) => {
  const {
    showModal,
    setShowModal,
    existingDmAutomation,
    existingDmTriggers,
    existingDmContents,
    initialEditContent,
    initialPlatform,
    initialTrigger,
  } = props;
  const { campaigns } = useSelector(getCampaignState);
  const { uid, username } = useSelector(getAccountState).account.accountData;
  // Editing existing automation will have an id, while clone automation will not.
  const isEditing = !!existingDmAutomation?.id;
  const [stage, setStage] = useState<CreateDMAutomationStage>(
    isEditing
      ? CreateDMAutomationStage.AUTOMATION
      : CreateDMAutomationStage.TRIGGER
  );
  const [isEditingTrigger, setIsEditingTrigger] = useState(false);
  const [newTriggerItem, setNewTriggerItem] = useState<DMAutomationTrigger>(
    existingDmTriggers?.[0]
      ? existingDmTriggers[0]
      : initialTrigger && !existingDmAutomation
        ? initialTrigger
        : makeEmptyDMAutomationTrigger(
            uid,
            existingDmAutomation ? existingDmAutomation.id : "",
            InstagramTrigger.COMMENT_POST_REEL,
            "",
            initialPlatform ?? DMAutomationPlatform.INSTAGRAM
          )
  );
  // Selected Specific Post/Reel's caption, for AI-generating the automation's default name on create (MARK-3887)
  const [postCaption, setPostCaption] = useState<string | undefined>(undefined);
  // Finds the DM_MESSAGE content, if it exists.
  const existingDmMessageContent = existingDmContents?.find(
    (content) => content.type === DMAutomationContentType.DM_MESSAGE
  );
  const [newContentItem, setNewContentItem] = useState<DMAutomationContent>(
    existingDmMessageContent ??
      makeEmptyDMAutomationContent(
        uid,
        existingDmAutomation ? existingDmAutomation.id : "",
        username,
        generate()
      )
  );
  const [promptContents, setPromptContents] = useState<DMAutomationContent[]>(
    existingDmContents?.filter(
      (content) => content.type !== DMAutomationContentType.DM_MESSAGE
    ) ?? []
  );
  const [newMassText, setNewMassText] = useState<MassText>(
    makeEmptyMassText(generate(), uid, username, {
      message: subscribeTriggerPlaceholder(),
      type: MassTextType.TRIGGERTEXT,
    })
  );
  // Single state for both content and mass text message to share the stored message
  const [initialMessage, setInitialMessage] = useState(
    existingDmMessageContent?.message ?? subscribeTriggerPlaceholder()
  );
  const [massTextAttachments, setMassTextAttachments] = useState<
    MassTextAttachment[]
  >([]);
  const [loadingMassText, setLoadingMassText] = useState(false);
  const [sendAsText, setSendAsText] = useState(
    existingDmAutomation ? !!existingDmAutomation.campaignId : true
  );
  const [requireFollow, setRequireFollow] = useState(
    existingDmAutomation ? existingDmAutomation.requireFollow : false
  );
  const [removeCompliance, setRemoveCompliance] = useState(
    existingDmAutomation ? existingDmAutomation.removeCompliance : false
  );

  const contentForPanel = useMemo<DMAutomationContent>(
    () => ({ ...newContentItem, message: initialMessage }),
    [newContentItem, initialMessage]
  );
  const massTextForPanel = useMemo<MassText>(
    () => ({ ...newMassText, message: initialMessage }),
    [newMassText, initialMessage]
  );

  useAsyncOnMount(async () => {
    if (isEditing) {
      // Shortcut to the message editor if content block is pressed on dashboard
      if (initialEditContent) {
        setStage(CreateDMAutomationStage.CONTENT);
      } else {
        setStage(CreateDMAutomationStage.AUTOMATION);
      }
    }

    // Prefill the message for a campaign-based automation, whether editing a real one or reviewing a clone of one.
    if (existingDmAutomation?.campaignId) {
      setLoadingMassText(true);
      const result = await fetchCampaignInitialMassText(
        uid,
        existingDmAutomation.campaignId,
        campaigns
      );
      if (result) {
        const { massText, attachments } = result;
        setNewMassText(massText);
        setInitialMessage(massText.message);
        setMassTextAttachments(attachments);
      } else {
        // Failed to fetch the initial mass text, so we reload the page to try again.
        alert(
          "Failed to load the existing message — the editor may not reflect what's currently saved. This page will reload."
        );
        window.location.reload();
      }
      setLoadingMassText(false);
    }
  });

  /**
   * Placeholder DMAutomation used when creating a brand-new automation.
   * State is DRAFT so the side panel button shows "Publish".
   */
  const dmAutomationForPanel = useMemo<DMAutomation>(() => {
    if (existingDmAutomation) {
      return existingDmAutomation;
    }
    return {
      id: "",
      createdAt: new Date().toISOString(),
      createdBy: uid,
      updatedAt: new Date().toISOString(),
      title: "",
      platform: initialPlatform ?? DMAutomationPlatform.INSTAGRAM,
      state: DMAutomationState.DRAFT,
      requireFollow: false,
      campaignId: "",
      removeCompliance: false,
      igAccountIds: [],
    };
  }, [existingDmAutomation, uid, initialPlatform]);

  const closeCreateTriggerPanel = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const editMessage = useCallback(() => {
    if (sendAsText) {
      setStage(CreateDMAutomationStage.MASS_TEXT);
    } else {
      setStage(CreateDMAutomationStage.CONTENT);
    }
  }, [sendAsText]);

  return (
    <LargePopupModalContainer
      showModal={showModal}
      containerStyles={{ padding: 0, height: "100%" }}
      valueComp={
        stage === CreateDMAutomationStage.TRIGGER ? (
          <DMAutomationCreateTrigger
            triggerItem={newTriggerItem}
            setTriggerItem={setNewTriggerItem}
            dmAutomation={dmAutomationForPanel}
            selectTriggerItem={
              isEditingTrigger
                ? () => {
                    setIsEditingTrigger(false);
                    setStage(CreateDMAutomationStage.AUTOMATION);
                  }
                : closeCreateTriggerPanel
            }
            deleteTriggerOnPress={
              isEditingTrigger
                ? () => {
                    setIsEditingTrigger(false);
                    setStage(CreateDMAutomationStage.AUTOMATION);
                  }
                : closeCreateTriggerPanel
            }
            nextOnPress={
              isEditingTrigger
                ? undefined
                : () => setStage(CreateDMAutomationStage.AUTOMATION)
            }
            onPostCaptionChange={setPostCaption}
          />
        ) : stage === CreateDMAutomationStage.CONTENT ? (
          <DMAutomationContentPanel
            dmAutomationContent={contentForPanel}
            isEditing={isEditing}
            backOnPress={() => setStage(CreateDMAutomationStage.AUTOMATION)}
            onSaveContent={(content) => {
              setNewContentItem(content);
              setInitialMessage(content.message);
              setStage(CreateDMAutomationStage.AUTOMATION);
            }}
          />
        ) : stage === CreateDMAutomationStage.MASS_TEXT ? (
          <DMAutomationMassTextPanel
            initialMassText={massTextForPanel}
            initialMassTextAttachments={massTextAttachments}
            isEditing={isEditing}
            backOnPress={() => setStage(CreateDMAutomationStage.AUTOMATION)}
            onSaveContent={(massText) => {
              setNewMassText(massText);
              setInitialMessage(massText.message);
              setStage(CreateDMAutomationStage.AUTOMATION);
            }}
          />
        ) : stage === CreateDMAutomationStage.CUSTOMIZE_PROMPTS ? (
          <DMAutomationPromptsPanel
            dmAutomationId={dmAutomationForPanel.id}
            sendAsText={sendAsText}
            requireFollow={requireFollow}
            removeCompliance={removeCompliance}
            setRemoveCompliance={setRemoveCompliance}
            isEditing={isEditing}
            promptContents={promptContents}
            setPromptContents={setPromptContents}
            backOnPress={() => setStage(CreateDMAutomationStage.AUTOMATION)}
          />
        ) : (
          <CreateDMAutomation
            triggerItem={newTriggerItem}
            postCaption={postCaption}
            dmAutomationTriggers={isEditing ? existingDmTriggers : undefined}
            dmAutomationContent={contentForPanel}
            setDmAutomationContent={setNewContentItem}
            promptContents={promptContents}
            massText={massTextForPanel}
            massTextAttachments={massTextAttachments}
            loadingMassText={loadingMassText}
            dmAutomation={dmAutomationForPanel}
            isEditing={isEditing}
            dmAutomationSettings={{
              sendAsText: sendAsText,
              requireFollow: requireFollow,
              removeCompliance: removeCompliance,
              initialMessage: initialMessage,
            }}
            setDmAutomationSettings={({
              sendAsText,
              requireFollow,
              removeCompliance,
              initialMessage,
            }) => {
              setSendAsText(sendAsText);
              setRequireFollow(requireFollow);
              setRemoveCompliance(removeCompliance);
              setInitialMessage(initialMessage);
            }}
            backOnPress={
              isEditing
                ? closeCreateTriggerPanel
                : () => setStage(CreateDMAutomationStage.TRIGGER)
            }
            onEditTrigger={(trigger) => {
              setNewTriggerItem(trigger);
              setIsEditingTrigger(true);
              setStage(CreateDMAutomationStage.TRIGGER);
            }}
            onEditMessage={editMessage}
            onEditCustomizePrompts={() =>
              setStage(CreateDMAutomationStage.CUSTOMIZE_PROMPTS)
            }
            onPublishSuccess={() => setShowModal(false)}
          />
        )
      }
    />
  );
};

export default memo(CreateDMAutomationWrapper);
